import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiFetch } from "@/lib/api-client";

export interface BrandListItem {
  id: string;
  name: string;
  slug: string;
  role: string;
  kit: { logoUrl: string | null; colors: Record<string, string> | null } | null;
  accountCount: number;
}

export interface BrandDetail {
  id: string;
  name: string;
  slug: string;
  orgId: string;
  kit: {
    colors: Record<string, string> | null;
    fonts: Record<string, string> | null;
    logoUrl: string | null;
    voiceProfile: string | null;
  } | null;
  members: Array<{ userId: string; name: string; email: string; role: string }>;
  accounts: Array<{ id: string; platform: string; username: string; status: string }>;
  zernioConnected: boolean;
  zernioKeySource: "brand" | "env" | "none";
  currentUserRole: string;
  createdAt: string;
}

/**
 * Mirror of the backend's slugify in `apps/api/src/routes/brands.ts`.
 * Keep these in sync — the preview is only meaningful if it predicts what
 * the backend will actually produce.
 */
export function slugify(input: string): string {
  return input
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");
}

export function useBrands() {
  return useQuery({
    queryKey: ["brands"],
    queryFn: () => apiFetch<BrandListItem[]>("/brands"),
  });
}

export function useBrand(brandId: string) {
  return useQuery({
    queryKey: ["brand", brandId],
    queryFn: () => apiFetch<BrandDetail>(`/brands/${brandId}`),
    enabled: !!brandId,
  });
}

export function useCreateBrand() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { name: string }) =>
      apiFetch<{ id: string; slug: string; name: string }>("/brands", {
        method: "POST",
        body: JSON.stringify(input),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["brands"] }),
  });
}

export function useUpdateBrand(brandId: string) {
  const qc = useQueryClient();
  return useMutation({
    // Backend returns the raw brand row (not the enriched BrandDetail), so
    // we type only what we need.
    mutationFn: (input: { name?: string }) =>
      apiFetch<{ id: string; name: string; slug: string }>(`/brands/${brandId}`, {
        method: "PATCH",
        body: JSON.stringify(input),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["brand", brandId] });
      qc.invalidateQueries({ queryKey: ["brands"] });
    },
  });
}

export function useDeleteBrand() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (brandId: string) =>
      apiFetch<{ id: string; deleted: true }>(`/brands/${brandId}`, {
        method: "DELETE",
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["brands"] }),
  });
}
